import { ApiProvider, legacyRedirect, NodusClient, setDefaultClient, shellApi, shouldRetry, WatchManager } from "@nodus/api";
import { LinkProvider, PrefsProvider, TooltipProvider, type LinkComponent } from "@nodus/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, Link, RouterProvider } from "@tanstack/react-router";
import * as React from "react";
import { createRoot } from "react-dom/client";
import { consoleMask, expandConsoleURL, registerConsolePaths } from "./app/console-url";
import { setRegisteredPaths } from "./app/home";
import { RuntimeProvider } from "./app/runtime";
import { ShellStateProvider } from "./app/shell-state";
import type { Auth } from "./auth/auth";
import { AuthProvider } from "./auth/auth-context";
import { loadRuntimeConfig } from "./config";
import type { RouterContext } from "./router-context";
import { routeTree } from "./routeTree.gen";
import "./styles.css";

// Created at module scope so its type registers below; the boot-time services arrive as RouterProvider context.
const router = createRouter({
  routeTree,
  context: undefined! as RouterContext,
  defaultPreload: "intent",
  defaultPendingMs: 300,
  scrollRestoration: true,
});
setRegisteredPaths(Object.keys(router.routesByPath));
registerConsolePaths(Object.keys(router.routesByPath));
router.update({ ...router.options, rewrite: { input: ({ url }) => expandConsoleURL(url) } });

/**
 * In-app hrefs (from linkTo, with `?focus=` and `#L<n>`) become router navigations with typed search and hash;
 * anything off this origin stays a plain anchor.
 */
const RouterLink: LinkComponent = ({ href, ref, ...rest }) => {
  const url = new URL(href, window.location.href);
  if (url.origin !== window.location.origin) return <a href={href} ref={ref} {...rest} />;
  return (
    <Link
      to={url.pathname}
      mask={consoleMask(url.href)}
      search={router.options.parseSearch(url.search)}
      hash={url.hash.replace(/^#/, "") || undefined}
      activeOptions={"aria-current" in rest ? { exact: true } : undefined}
      ref={ref}
      {...rest}
    />
  );
};

/** Pauses watches for tabs hidden longer than 5 minutes and resumes from the last resourceVersion (ui-spec §4.1). */
function pauseWhenHidden(watches: WatchManager) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  document.addEventListener("visibilitychange", () => {
    clearTimeout(timer);
    if (document.visibilityState === "hidden") timer = setTimeout(() => watches.pauseAll(), 5 * 60_000);
    else watches.resumeAll();
  });
}

/**
 * Legacy console URLs that are not `/`-rooted (`/console/…`, `/sandboxes/:id`) would otherwise match `/$org`; they
 * are rewritten before the router starts (ADR-080). `/?view=` shapes are resolved by the `/` route, which knows the
 * user's orgs.
 */
function rewriteLegacyPath() {
  const url = new URL(window.location.href);
  if (/^\/console(\/|$)/.test(url.pathname)) {
    url.pathname = url.pathname.replace(/^\/console/, "") || "/";
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  }
  const current = new URL(window.location.href);
  // The current create page takes precedence over the legacy sandbox-id route.
  if (current.pathname.replace(/\/$/, "") === "/sandboxes/new") return;
  const legacy = legacyRedirect(current);
  if (legacy && !legacy.needsOrg) window.history.replaceState(null, "", legacy.to("", ""));
}

async function boot() {
  rewriteLegacyPath();
  const config = await loadRuntimeConfig();
  let auth: Auth;
  if (config.environment === "mock") {
    const { startMock } = await import("./mock/browser");
    const store = await startMock();
    const { MockAuth } = await import("./auth/mock-auth");
    auth = new MockAuth(store, { captcha: Boolean(config.turnstileSiteKey) });
  } else {
    const { SupabaseAuth } = await import("./auth/supabase-auth");
    auth = new SupabaseAuth(config.supabaseURL!, config.supabaseAnonKey!);
  }

  const client = new NodusClient({
    baseURL: config.apiURL,
    getToken: async () => (await auth.getSession())?.accessToken,
    refreshToken: async () => (await auth.refresh())?.accessToken,
  });
  // The HTTP/1 local proxy has six browser connections; watches would starve navigation and API reads.
  const pollInterval = import.meta.env.DEV && config.apiURL === `${window.location.origin}/__nodus_api` ? 10_000 : undefined;
  setDefaultClient(client);
  const watches = new WatchManager(client);
  pauseWhenHidden(watches);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { retry: (n, err) => shouldRetry(err, n), refetchOnWindowFocus: false, staleTime: 30_000 },
      mutations: { retry: false },
    },
  });
  // A different user signing in clears everything cached for the previous one (ui-spec §4.18).
  let userId: string | undefined;
  auth.onChange((s) => {
    if (s?.user.id !== userId) queryClient.clear();
    userId = s?.user.id;
  });

  const context: RouterContext = { config, auth, client, api: shellApi(client), watches, queryClient };
  createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <RuntimeProvider value={context}>
        <PrefsProvider {...(config.cookieDomain ? { cookieDomain: config.cookieDomain } : {})}>
          <AuthProvider auth={auth}>
            <QueryClientProvider client={queryClient}>
              <ApiProvider client={client} watches={watches} pollInterval={pollInterval}>
                <TooltipProvider>
                  <LinkProvider component={RouterLink}>
                    <ShellStateProvider>
                      <RouterProvider router={router} context={context} />
                    </ShellStateProvider>
                  </LinkProvider>
                </TooltipProvider>
              </ApiProvider>
            </QueryClientProvider>
          </AuthProvider>
        </PrefsProvider>
      </RuntimeProvider>
    </React.StrictMode>,
  );
}

declare module "@tanstack/react-router" {
  interface HistoryState {
    publicConsoleURL?: boolean;
  }
  interface Register {
    router: typeof router;
  }
}

void boot().catch(() => {
  createRoot(document.getElementById("root")!).render(
    <main role="alert" className="mx-auto max-w-lg p-8">
      <h1 className="text-xl font-semibold">Console unavailable</h1>
      <p className="mt-3">The console could not load. Reload the page to try again.</p>
      <button type="button" className="mt-4 underline" onClick={() => window.location.reload()}>
        Reload
      </button>
    </main>,
  );
});
